import { ICourse2 } from "@et/types/Courses";
import { PAYMENT_TYPES_THEME } from "@et/types/Enums";
import { neonPink } from "@styles/themes/darkThemeBeautifulLettering";
import { graphql } from "gatsby";
const slug = 'procreate-patterns-df'
const title = 'Floral Patterns in Procreate'
const highlight1 = `#B77DC3`
const mainColor = `#4D286F`
const course: ICourse2 = {
  theme: PAYMENT_TYPES_THEME.LIGHT,
  title,
  slug,
  videoId: `nfb07pgvxs`,
  buyNowPayLater: null,
  keywords: [
    'procreate patterns', 'repeat pattern', 'seamless pattern', 'how to create patterns in procreate', 'flower doodles', 'draw flowers', 'iPad drawing', 'patterns in nature', 'spoonflower', 'pattern design', 'pattern for art', 'how to draw flowers in procreate', 'textile designs', 'surface design', 'pattern and design', 'repeating pattern', 'types of repeat patterns', 'surface pattern design', 'types of pattern designs', 'repeat pattern design', 'repeated pattern', 'procreate course'
  ],
  price: '197',
  salePrice: '147',
  paymentPlan: {
    price: '75',
    perMonth: '3 Payments of $75/m',
    savings: '10%',
    salePrice: '67',
  },
  motionMail: `https://s.mmgo.io/t/CiEB/scale_2x`,
  onSale: true,
  tagline: 'Create beautiful patterns directly in Procreate!',
  classDescriptor: 'Create beautiful patterns directly in Procreate!',
  intro: {
    imgName: 'procreate301/tablet-intro.png',
    imgAlt: 'Create custom modern lettering styles that are unique to you',
    bullets: [
      {
        title: 'Beginner Friendly',
        desc:
          'Step-by-step narration with closeups on all the details, so you feel confident in the process from start to finish.',
      },
      {
        title: 'A Versatile Process',
        desc:
          'Since every lettering style is different, this process has been fine-tuned to fit any situation. You’ll be able to achieve a realistic watercolor effect when you apply it to any style - hand lettered or typed.',
      },
      {
        title: 'Blend Options',
        desc:
          'You may want an ombré, one color, smooth transitions blend with one of your pieces and a multi-colored blend with water pockets on another. This course serves up 5 different blend options, so you’ll have plenty to choose from when you begin your next lettering piece.',
      },
      {
        title: 'Florals + Foliage Integration',
        desc:
          'Take your watercolor lettering even further by integrating florals and foliage! A bonus florals foundations module is included, so even if you’ve never painted florals before, you’ll be able to follow along and complete every project in the class.',
      },
    ],
  },
  projects: {
    beginner: [
      {
        title: 'Project 1',
        backgroundColor: `#c4d8da`,
        textColor: `#4a5457`,
        subheadColor: `#fff`,
        projectImg: `procreate301/project-1.jpg`,
        projectImgMobile: `procreate301/project-1.jpg`,
        type: '',
        desc:
          'We kick things off with a seashell symmetry full drop pattern. We\'ll set up our template together using the symmetry block brushes, paint in the elements, then make all edges seamless through pre-programmed selections. Once we have all of our gaps filled in, we\'ll drag it into the Pattern Playground for a final test.',
      },
      {
        title: 'Project 2',
        backgroundColor: `#fcbfb2`,
        textColor: `#4a5457`,
        subheadColor: `#fcb900`,
        projectImg: `procreate301/project-2.jpg`,
        projectImgMobile: `procreate301/project-2-mobile.jpg`,
        type: '',
        desc:
          'To reinforce what we covered in Project 1, we\'ll use another symmetry block brush - this time the flower shape, and create another full drop pattern. I share how I fix troublesome areas when elements moving in different directions collide and I explain my full thought process along with my reasons behind every decision I make. We reconfigure our pattern to make it seamless using our pre-programmed selections and do a final test using the Pattern Playground.',
      },
      {
        title: 'Project 3',
        backgroundColor: `#ffe3df`,
        textColor: `#4a5457`,
        subheadColor: `#f3ecdf`,
        projectImg: `procreate301/project-3.jpg`,
        projectImgMobile: `procreate301/project-3-mobile.jpg`,
        type: '',
        desc: 'In Project 3, we create a full drop repeat using an included composition template. Following the composition template, we\'re able to speed up our workflow and quickly adjust and adapt it to fit our needs. Once our elements are in place, we use our pre-programmed selections within the template to fill in the gaps, then test + adjust as needed using the Pattern Playground.',
      },
      {
        title: 'Project 4',
        projectImg: `procreate501/project-4.jpg`,
        projectImgMobile: `procreate301/project-4-mobile.jpg`,
        backgroundColor: `#dee5dd`,
        textColor: `#565857`,
        subheadColor: `#c56b00`,
        type: '',
        desc:
          'It\'s time to head into half drop patterns in Project 4! We\'ll work with a different composition template this time, pre-programmed with selections and set up specifically for half drops. After painting our elements in, we\'ll reorganize the template to fill in gaps using our selections, then test + adjust using the Pattern Playground.',
      },
      {
        title: 'Project 5',
        projectImg: `procreate301/project-5.jpg`,
        projectImgMobile: `procreate301/project-5-mobile.jpg`,
        backgroundColor: `#ffd5bc`,
        textColor: `#4a5457`,
        subheadColor: `#419187`,
        type: '',
        desc:
          'To round out our pattern types, Project 5 is all about the half brick. We\'ll pick a half brick specific composition template, lay in our artwork and go off script from the template as needed. We then reorganize our artwork using the pre-programmed selections to fill in gaps and test our completed half brick within the Pattern Playground.',
      },
    ],
    advanced: [],
  },
  curriculum: [
    {
      title: 'Welcome',
      videos: [
        {
          title: 'What You\'ll Learn/Course Overview',
          duration: '8:45',
        },
        {
          title: 'My Tools + Accessories',
          duration: '1:43',
        },
        {
          title: 'Gestures We\'ll Use',
          duration: '0:57',
        },
        {
          title: 'Bonus Location/Install Instructions: Brushes + Color',
          duration: '5:25',
        },
        {
          title: 'Bonus Location/Install Instructions: Procreate Template Files',
          duration: '7:17',
        }
      ],
    },
    {
      title: 'Common Pattern Types + Styles: Overview',
      videos: [
        {
          title: 'Full Drop Overview',
          duration: '2:01',
        },
        {
          title: 'Half Drop + Half Brick Overview',
          duration: '3:27',
        },
        {
          title: 'Pattern Styles Overview',
          duration: '2:44',
        }
      ]
    },
    {
      title: 'Creating Floral Pattern Design Elements',
      videos: [
        {
          title: 'How to Draw Easy Flowers: Part 1',
          duration: '11:29',
        },
        {
          title: 'How to Draw Easy Flowers: Part 2',
          duration: '9:44',
        },
        {
          title: 'How to Draw Easy Flowers: Part 3',
          duration: '10:07',
        },
        {
          title: 'How to Draw Easy Foliage',
          duration: '5:18',
        },
      ],
    },
    {
      title: 'Setting Up Your Canvas',
      videos: [
        {
          title: 'Size, Color Profile + Resolution',
          duration: '2:41',
        }
      ],
    },
    {
      title: 'Full Drop Pattern: Method 1',
      videos: [
        {
          title: 'Create the Artwork',
          duration: '2:42',
        },
        {
          title: 'Preview the Repeat',
          duration: '3:49',
        },
      ],
    },
    {
      title: 'Using the Pattern Playground',
      videos: [
        {
          title: 'Pattern Playground Overview',
          duration: '4:03',
        }
      ],
    },
    {
      title: 'Full Drop Pattern: Method 2',
      videos: [
        {
          title: 'Create the Artwork + Preview the Repeat with the Pattern Playground',
          duration: '5:39',
        }
      ],
    },
    {
      title: '[Project 1 + 2] Using Symmerty Pattern Template Brushes',
      videos: [
        {
          title: 'Symmetry Pattern Template Brushes: Overview',
          duration: '3:42',
        },
        {
          title: 'Project 1: Seashell Pattern: Create the Artwork Part 1',
          duration: '4:28',
        },
        {
          title: 'Project 1: Seashell Pattern: Create the Artwork Part 2',
          duration: '2:58',
        },
        {
          title: 'Project 1: Seashell Pattern: Make it Seamless + Test',
          duration: '3:10',
        },
        {
          title: 'Project 2: Flower Symmetry Pattern: Create the Artwork',
          duration: '11:19',
        },
        {
          title: 'Project 2: Flower Symmetry Pattern: Make it Seamless + Test',
          duration: '5:17',
        },
      ],
    },
    {
      title: 'How to Use Pattern Composition Templates',
      videos: [
        {
          title: 'Blank Templates with Selections: Overview',
          duration: '1:45',
        },
        {
          title: 'Pattern Composition Templates: Overview',
          duration: '3:11',
        },
        {
          title: 'Adapting the Templates for Other Canvas Sizes: Full Drop (blank)',
          duration: '4:07',
        },
        {
          title: 'Adapting the Templates for Other Canvas Sizes: Half Drop + Half Brick (blank)',
          duration: '5:58',
        },
        {
          title: 'Adapting the Templates for Other Canvas Sizes: Pattern Compositions',
          duration: '3:04',
        }
      ],
    },
    {
      title: '[Project 3] Seamless Full Drop Pattern Using Composition Template',
      videos: [
        {
          title: 'Selecting the Composition + Creating the Artwork',
          duration: '8:49',
        },
        {
          title: 'Manipulating Selections + Completing the Artwork',
          duration: '4:00',
        },
        {
          title: 'Preview/Test the Pattern/Make Adjustments',
          duration: '5:36',
        },
      ]
    },
    {
      title: '[Project 4] Seamless Half Drop Pattern Using Composition Template',
      videos: [
        {
          title: 'Selecting the Composition + Creating the Artwork',
          duration: '11:09',
        },
        {
          title: 'Manipulate Selections + Completing the Artwork',
          duration: '6:42',
        },
        {
          title: 'Preview/Test the Pattern/Make Adjustments',
          duration: '8:18',
        },
      ]
    },
    {
      title: '[Project 5] Half Brick Pattern',
      videos: [
        {
          title: 'Selecting the Composition + Creating the Artwork',
          duration: '4:47',
        },
        {
          title: 'Manipulate Selections, Complete Artwork + Test the Pattern',
          duration: '5:24',
        }
      ]
    },
    {
      title: 'Bonus Project: Dandelion Half Brick Pattern + Creating Multiple Colorways',
      videos: [
        {
          title: 'Selecting the Composition + Creating the Artwork',
          duration: '4:37',
        },
        {
          title: 'Manipulate Selections, Complete Artwork + Test the Pattern',
          duration: '2:52',
        },
        {
          title: 'Recoloring Patterns/Creating Multiple Colorways',
          duration: '6:24',
        }
      ]
    },
    {
      title: 'Bonus: Creating Simple Pattern Mockups in Procreate',
      videos: [
        {
          title: 'Tshirt Mockup',
          duration: '8:07',
        },
        {
          title: 'Tote Mockup',
          duration: '9:06',
        },
        {
          title: 'Notebook Mockup from Photo',
          duration: '9:26',
        }
      ]
    },
    {
      title: 'Bonus: Exporting Your Patterns + Getting Them Printed',
      videos: [
        {
          title: 'Export the Pattern Square',
          duration: '2:12',
        },
        {
          title: 'Uploading to Spoonflower',
          duration: '1:07',
        },
        {
          title: 'Ordering Fabric from Spoonflower',
          duration: '4:10',
        },
        {
          title: 'Ordering Wallpaper from Spoonflower',
          duration: '4:05',
        },
        {
          title: 'Print Quality Examples',
          duration: '10:41',
        },
        {
          title: 'Applying Peel + Stick Wallpaper!',
          duration: '2:42',
        },
      ]
    },
    {
      title: 'Converting Your Procreate Patterns Into Photoshop .PAT File & Illustrator Swatches',
      videos: [
        {
          title: 'Convert your Full Drop Procreate Patterns into (rasterized) Illustrator Swatches',
          duration: '5:35',
        },
        {
          title: 'Convert your Half Drop and Half Brick Procreate Patterns into (rasterized) Illustrator Swatches',
          duration: '5:26',
        },
        {
          title: 'How to Save (and Export!) your Illustrator Swatches',
          duration: '4:32',
        },
        {
          title: 'Convert your Full Drop Procreate Patterns into Photoshop Patterns',
          duration: '2:37',
        },
        {
          title: 'Convert your Half Drop and Half Brick Procreate Patterns into Photoshop Patterns',
          duration: '6:10',
        },
        {
          title: 'How to Save (and Export!) a Photoshop .pat file',
          duration: '2:26',
        },
      ]
    },
    {
      title: 'Thank you + next steps',
      videos: [
        {
          title: 'Thank You + Next Steps',
          duration: '3:44',
        },
      ],
    },
  ],
  bonuses: {
    headerImgName: `procreate301/bonus-title.png`,
    imgAlt: 'Messy Watercolors in Procreate Bonuses',
    gridType: 1,
    items: [
      {
        title: 'Ink Lovers Brush Set + Extended License',
        desc: '<p>The full 12 piece drawing brushset is included with enrollment so you can follow along exactly. Add eye candy ink textures: from subtle, to scratchy to washed out and bold, you\'ll find everything you need to draw florals and foliage with charm. An extended license is included, so you can use the brushes to create artwork you plan to sell without limit.</p>',
        imgName: 'procreate-patterns/bonuses/bonus-1.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Symmetry Block Stamp Brushes',
        desc: '<p>A set of 8 shape-themed symmetry block brushes are included to make the symmetry block style a breeze. Check out projects 1 + 2 for creative ideas to get started with them!</p>',
        imgName: 'procreate-patterns/bonuses/bonus-2.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Composition Canvas Templates',
        desc: '<p>5 full drop, half drop and half brick floral-themed templates for a total of 15 pre-programmed canvases. Every template includes built in selections and designs, so you have what you need with as much guidance and flexibility desired for new patterns.</p>',
        imgName: 'procreate-patterns/bonuses/bonus-3.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: "3 'Blank' Canvas Templates",
        desc: '<p>With three canvas templates all pre-programmed with selections for full drop, half drop and half brick repeats, you have the freedom to create any pattern you\'d like and still have all the benefits of this seamless process.</p>',
        imgName: 'procreate-patterns/bonuses/bonus-4.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Adaptable Templates',
        desc: '<p>Now that you\'re equipped with all the selections and templates needed, check out the full module on adapting those templates to different canvas sizes. This way, you can continue using them for any purpose or project guidelines you have. Versatility? Check ✅</p>',
        imgName: 'procreate-patterns/bonuses/bonus-5.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'All Color Palettes',
        desc: '<p>Every practice exercise and project\'s color palette is included, so you\'ll be able to follow each video and project perfectly.</p>',
        imgName: 'procreate-patterns/bonuses/bonus-6.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Pattern Playground Guide',
        desc: '<p>An in-depth video overview with examples of how the Pattern Playground works, so you can make it work best for you and all the gorgeous patterns you\'re about to make.</p>',
        imgName: 'procreate-patterns/bonuses/bonus-7.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Pattern Making Cheat Sheets',
        desc: '<p>Pick up the pattern types cheat sheet and the easy flowers + foliage reference guide, so you have all the elements and know-how of building your future repeats at the ready.</p>',
        imgName: 'procreate-patterns/bonuses/bonus-8.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Private Facebook Community',
        desc: '<p>A group of pattern loving, Procreate creatin\' creatives to design beautiful art alongside. The prefect place to find answers to questions, get feedback on your work, and a non stop source of support from the kindest Procreate makers. I\'m always in there, too!</p>',
        imgName: 'procreate-patterns/bonuses/bonus-9.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Mock it up in Procreate',
        desc: '<p>Learn how to use Photoshop mockup files directly in Procreate without using Photoshop! You\'ll also learn how to convert any stock image into a Procreate mockup you can use and reuse, too!</p>',
        imgName: 'procreate-patterns/bonuses/bonus-10.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Exporting + Printing Module',
        desc: '<p>Never worry about exporting or uploading the right file type or color profile when it comes to printing fabric or wallpaper through Spoonflower! I take you through every step, share my favorite fabric types, do color comparisons between sample prints vs. files and share a full timelapse of applying peel + stick wallpaper within my home, narrated with my experiences and helpful advice.</p>',
        imgName: 'procreate-patterns/bonuses/bonus-12.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Procreate to Ai Swatches + .PAT files',
        desc: '<p>In this bonus module, I\'ll guide you through how to convert your Procreate pattern square into a rasterized Illustrator swatch file that can be reused in future Illustrator docs as well as converting each pattern type into Photoshop .PAT files, so you can use (or sell!) your patterns as seamless repeats in both programs!</p>',
        imgName: 'procreate-patterns/bonuses/bonus-11.jpg',
        bgColor: '#FFF6EE'
      },
    ],
  },
  courseCard: {
    title: 'Messy Watercolors in Procreate',
    backgroundImgName: ``,
    backgroundColor: '#65807c',
    cardImgName: `procreate301/watercolor-class-card-4.jpg`,
    desc: 'Paint vibrant and messy eye candy watercolor artwork in Procreate! Reference traditional methods while utilizing digital advantages like blend modes, selective color adjustments and layers. Follow along as each project builds in complexity with technique, process and levels of messiness. In addition to painting, you’ll learn and use multiple layers, blend modes, selections, recoloring, saturation adjustments and warping to create stunning artwork using an efficient process. Finish the course by learning how to prepare your artwork for print using a home printer or online print services to enjoy your art beyond the screen.',
    bullets: [
      'Ink Lovers Brush Set',
      'Symmetry Block Stamp Brushes',
      'All Practice and Project Color Palettes',
      'Pattern Types + Styles Overview',
      '4 Easy Florals + Foliage Drawing Tutorials',
      '2 Methods for Full Drop Repeats',
      'Pattern Playground (pattern tester + previewer)',
      '2 Symmetry Block Pattern Projects',
      '15 Pattern Composition Canvas Templates',
      '3 Blank Pattern Templates with Pre-Programmed Selections',
      'Tutorials for Adapting Templates to Any Canvas Size',
      'Full Drop Pattern Project using a Composition Template',
      'Half Drop Pattern Project using a Composition Template',
      '2 Half Brick Pattern Projects using Composition Templates',
      'Develop Multiple Pattern Colorways Tutorial',
      '3 Procreate Pattern Mockup Tutorials',
      'Exporting your Patterns + Getting them Printed Module',
      'Spoonflower Fabric + Wallpaper Video Buyer\'s Guide',
      'Wallpaper Application Timelapse with Narration',
      'How to Convert your Procreate Patterns into Rasterized Illustrator Swatches',
      'How to Convert your Procreate Patterns into Photoshop .PAT Files',
      'Pattern Types Cheat Sheet',
      'Easy Florals + Foliage Cheat Sheet',
      'Helpful Links Section with Pattern Making Online Resources',
      'Membership in Our Private Facebook Community',
    ],
    brush: {
      enabled: true,
      image: {
        pencil: `apple2-pencil.png`,
        shadow: `sell-card-pencil-reflection-light.png`,
      },
    },
    priceColor: mainColor,
    saleColor: neonPink,
    stats: {
      modules: `#cc836f`,
      videos: `#758384`,
      hours: `#a79e8d`,
    },
  },
  faqs: [
    {
      question: 'What if I\'ve never created a pattern in Procreate before?',
      answer: '<p>Then you will never experience the pain of tiling those pattern squares manually 🎉 In all seriousness, though, you\'ve come to the right place! You\'ll be starting with a process that\'s faster and more efficient than how it\'s been previously done. High five.</p>',
    },
    {
      question: 'I\'m brand new to Procreate, will I be able to follow along?',
      answer: '<p>The course was created with beginners in mind, though if you\'re brand spankin\' new to Procreate, check out my <a target="_blank" href="https://learn.every-tuesday.com/procreate-for-beginners">free course, Procreate for Beginners</a>, first and you\'ll cruise through more comfortably.</p>',
    },
    {
      question: 'What do I need to take this course?',
      answer: '<p>You\'ll need an iPad that supports pressure sensitivity along with a compatible stylus (<a target="_blank" href="https://support.apple.com/en-us/HT211029">check out this list</a>) and the Procreate app (v.5.3.1 or newer) installed. That\'s it!</p>',
    },
    {
      question: 'I don\'t think I have time for this?',
      answer: '<p>All Every Tuesday courses come with lifetime access, so you can take it at the time and pace that works best for you. It\'s yours for the long haul.</p>',
    },
    {
      question: 'Do you offer any refunds?',
      answer: '<p>This course is backed by our 14 day guarantee. If you want to stick with the old ways of creating patterns in Procreate, send us an email before the 14 days is up for a full refund.</p>',
    },
    {
      question: 'Do I have to have Illustrator or Photoshop to take this course?',
      answer: '<p>Nope! There\'s a bonus section for converting your patterns into Illustrator swatches and Photoshop pattern files, but it\'s completely optional. This course is allll about creating beautiful, repeat floral patterns in Procreate.</p>',
    },
    {
      question: 'Does this class come with subtitles/closed captions?',
      answer: '<p>Yep! English subtitles are included for every video</p>'
    },
    {
      question: 'How long do I get access?',
      answer: '<p>You buy something, you get to keep it around here. For good. For \'eva.</p>',
    },
    {
      question: 'Can I sell the class projects I recreate on my own?',
      answer: '<p>The projects in the class are my own original concepts + designs, so you can\'t recreate and sell them. You *can* use all the methods, templates, processes, colors and brushes to create your own original work and sell that, though - which, I\'m guessing is why you\'d want to take the class anyway 😉</p>',
    },
    {
      question: 'I have more questions, how can I get in touch?',
      answer: '<p>Head to <a target="_blank" href="https://every-tuesday.com/contact">my contact page</a> and drop me your Q\'s - I\'m just an email away.</p>',
    },
  ],
  testimonials: [],
  letsDoThis: {
    imgName: 'procreate301/lets-do-this-2.jpg',
    altTag: 'Enroll in Messy Watercolors for Procreate today!',
    text: 'Welcome to Flat Florals in Procreate, your complete guide to painting simplified, bold and beautiful florals.',
  },
  sellCard: {
    priceColor: '#5f1212',
    saleColor: neonPink,
    cardImg: '',
    saleLink: 'https://courses.every-tuesday.com/purchase?product_id=5416363',
    link: 'https://courses.every-tuesday.com/purchase?product_id=4719710',
    paymentPlanLink: 'https://courses.every-tuesday.com/purchase?product_id=4719712',
    customBtnBackgroundColor: 'var(--ff-teal-600)',
    h2Color: `#5f1212`,
    hoverColor: {
      h2: `#fff`,
      text: 'var(--ff-teal-600)',
      overlay: `var(--ff-teal-400)`,
      cardDescription: '#fff'
    },
    bullets: [
      'Instant access to all videos and bonuses',
      'Full Bouquet Maker brush set (150+ brushes)',
      'Bonus applications module (3 projects)',
      'Private Facebook community',
      'Extended License of the brush set ($40 value)',
      'Seamless patterns with transparent backgrounds process',
      'Rose simplification tutorial',
      'Composition design tutorials',
      '14 day money back guarantee',
      'Flat style cheat sheets (pdf)',
      'All project + demo color palettes',
      'English subtitles on all videos',
      'Basic + complex style practice exercises (12 total)',
    ],
  },
}
export const gqlFragmentProcreatePatternsWMCD_Images = graphql`
    fragment FragmentProcreatePatternsWMCD_Images on Query {
        WMCD1:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-1-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD2:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-2-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD3:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-3-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD4:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-4-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD5:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-5-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD6:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-6-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD7:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-7-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD8:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-8-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD9:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-9-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD10:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-10-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD11:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-11-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD12:imgixImage(url: "procreate-patterns/ripped-swatches/swatch-12-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }

    }
`
export const gqlFragmentProcreatePatternsWYL = graphql`
    fragment FragmentProcreatePatternsWYL on Query {
        wylGridImage1:imgixImage(url: "procreate-patterns/wyl/wyl-1-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage2:imgixImage(url: "procreate-patterns/wyl/wyl-2-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage3:imgixImage(url: "procreate-patterns/wyl/wyl-3-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage4:imgixImage(url: "procreate-patterns/wyl/wyl-4-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage5:imgixImage(url: "procreate-patterns/wyl/wyl-6-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage6:imgixImage(url: "procreate-patterns/wyl/wyl-5-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage7:imgixImage(url: "procreate-patterns/wyl/wyl-7-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage8:imgixImage(url: "procreate-patterns/wyl/wyl-8-min.png") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage9:imgixImage(url: "procreate-patterns/wyl/wyl-9-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage10:imgixImage(url: "procreate-patterns/wyl/wyl-10-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage11:imgixImage(url: "procreate-patterns/wyl/wyl-11-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage12:imgixImage(url: "procreate-patterns/wyl/wyl-12-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylSquareImage1:imgixImage(url: "procreate-patterns/wyl/wyl-square-a.jpg") {
            gatsbyImageData(width:600, layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:600,
              auto: "compress"
          }, srcSetMaxWidth: 600)
        }
        wylSquareImage2:imgixImage(url: "procreate-patterns/wyl/wyl-square-b.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 800
            })
        }
    }
`
export const gqlFragmentProcreatePatternsBrushTips = graphql`
    fragment FragmentProcreatePatternsBrushTips on Query {
        brushTip1:imgixImage(url: "procreate-patterns/brushes/brush-1-1-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2:imgixImage(url: "procreate-patterns/brushes/brush-1-2-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip3:imgixImage(url: "procreate-patterns/brushes/brush-1-3-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip4:imgixImage(url: "procreate-patterns/brushes/brush-1-4-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip5:imgixImage(url: "procreate-patterns/brushes/brush-1-5-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip6:imgixImage(url: "procreate-patterns/brushes/brush-1-6-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip7:imgixImage(url: "procreate-patterns/brushes/brush-1-7-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip8:imgixImage(url: "procreate-patterns/brushes/brush-1-8-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip9:imgixImage(url: "procreate-patterns/brushes/brush-1-9-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip10:imgixImage(url: "procreate-patterns/brushes/brush-1-10-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip11:imgixImage(url: "procreate-patterns/brushes/brush-1-11-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip12:imgixImage(url: "procreate-patterns/brushes/brush-1-12-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_1:imgixImage(url: "procreate-patterns/brushes/brush-2-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_2:imgixImage(url: "procreate-patterns/brushes/brush-2-2.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_3:imgixImage(url: "procreate-patterns/brushes/brush-2-3.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_4:imgixImage(url: "procreate-patterns/brushes/brush-2-4.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_5:imgixImage(url: "procreate-patterns/brushes/brush-2-5.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_6:imgixImage(url: "procreate-patterns/brushes/brush-2-6.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_7:imgixImage(url: "procreate-patterns/brushes/brush-2-7.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip2_8:imgixImage(url: "procreate-patterns/brushes/brush-2-8.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
    }
`

export default course